import { useCallback } from 'react';
import classNames from 'classnames';

import { useMembershipOrderTbbContext } from '../MembershipOrderTbbContext';
import MEMBERSHIP_OFFERED_ITEMS from '../../../utils/membership';
import AddressInfo from '../../../global/AddressInfo';
import useScrollToTop from '../../../utils/hooks/useScrollToTop';
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '../../../global/Button';
import RewardsV2Shelf, { REWARDS_V2_SHELF_TYPES } from '../../../global/RewardsV2Shelf';
import { REWARD_PREVIEW_TYPES } from '../../../config/rewards';
import { SLIDER_TRACK_PADDING } from '../../../config/config';
import TCCoin from '../../../images/trashie-cash-v3.svg';
import PartyPopper from '../../../images/party-popper.svg';
import GiftCardsPreviewList from '../../GiftCards/GiftCardsPreviewList';
import UnlimitedShortcutBannerCard from '../../TakeBackBags/UnlimitedShortcutBannerCard';
import { RECYCLING_PRODUCT_TYPES } from '../../../utils/tbbRegistration';
import { useFirebaseAuth } from '../../../../global/FirebaseProvider/FirebaseProvider';
import './OrderConfirmation.scss';

const TCCoinIcon = () => (
  <img src={TCCoin} alt="TCCoin" width={24} height={24} />
);

const OrderConfirmation = ({
  showCta = false,
  showPromoSection = false,
}) => {
  useScrollToTop();
  const {
    selectedItems,
    selectedAddress = [],
    resetOrder,
    isOneTimeOrder,
    isFirstOrder,
    isMobileSize,
    messages,
  } = useMembershipOrderTbbContext();

  const { userData, isSignedIn } = useFirebaseAuth();
  const isActiveSubscription = userData?.activeSubscription;
  const selectedItemsArray = Object.entries(selectedItems).filter(([productType, quantity]) => {
    const productInfo = MEMBERSHIP_OFFERED_ITEMS[productType];
    return !!productInfo && quantity > 0;
  });

  const getOrderMessages = useCallback(() => {
    if (Object.keys(messages).length === 0) {
      return null;
    }

    const hasBags = selectedItemsArray.some(
      ([productType]) => productType === RECYCLING_PRODUCT_TYPES.TEXTIL,
    );
    const hasTechbox = selectedItemsArray.some(
      ([productType]) => productType === RECYCLING_PRODUCT_TYPES.VIRTUAL,
    );
    const hasMixed = hasBags && hasTechbox;

    let messagesToShow = {};

    switch (true) {
      case hasMixed:
        messagesToShow = { ...messages.mixed };
        break;
      case hasBags:
        messagesToShow = { ...messages.bags };
        break;
      case hasTechbox:
        messagesToShow = { ...messages.techbox };
        break;
      default:
        return null;
    }

    return (selectedItemsArray.length > 0 && Object.keys(messages).length > 0) ? (
      <>
        <div className="OrderConfirmation__divider" />
        <div className="OrderConfirmation__messages">
          {(messagesToShow.message1 || messagesToShow.delivery1) && (
            <div className={classNames('OrderConfirmation__message', {
              oneLine: messagesToShow.message2 || messagesToShow.delivery2,
            })}
            >
              {messagesToShow.message1 && (
                <div className="OrderConfirmation__message-title">{messagesToShow.message1}</div>
              )}
              {messagesToShow.delivery1 && (
                <div className="OrderConfirmation__message-delivery">{messagesToShow.delivery1}</div>
              )}
            </div>
          )}
          {(messagesToShow.message2 || messagesToShow.delivery2) && (
            <div className="OrderConfirmation__message">
              {messagesToShow.message2 && (
                <div className="OrderConfirmation__message-title">{messagesToShow.message2}</div>
              )}
              {messagesToShow.delivery2 && (
                <div className="OrderConfirmation__message-delivery">{messagesToShow.delivery2}</div>
              )}
            </div>
          )}
        </div>
      </>
    ) : null;
  }, [selectedItemsArray, messages]);

  return (
    <div className="OrderConfirmation">
      <div className="OrderConfirmation__container">
        <div className={classNames('OrderConfirmation__card', {
          contained: !isFirstOrder && !isOneTimeOrder,
        })}
        >
          <div>
            <h1 className="OrderConfirmation__title">
              {isFirstOrder ? (
                <div className="OrderConfirmation__title--firstOrder--container">
                  <div className="OrderConfirmation__title--firstOrder--container-title">
                    Congrats
                  </div>
                  <div className="OrderConfirmation__title--firstOrder--container-image">
                    <img src={PartyPopper} alt="Party Popper" />
                  </div>
                </div>
              ) : 'Order placed, thanks!'}
            </h1>
            <p className="OrderConfirmation__subtitle">
              {isFirstOrder && selectedItemsArray.length > 0 ? 'Your first order is placed with your Trashie Unlimited!' : 'Check your email for the confirmation details.'}
            </p>
          </div>
          {getOrderMessages()}
          <div className="OrderConfirmation__divider" />
          <div className="OrderConfirmation__info">
            {Object.keys(selectedAddress).length > 0 && (
              <div className="OrderConfirmation__section">
                <h3 className="OrderConfirmation__section-title">
                  {`Shipping to ${selectedAddress?.firstName || ''} ${selectedAddress?.lastName || ''}`}
                </h3>
                <AddressInfo
                  {...selectedAddress}
                  firstName={null}
                  lastName={null}
                />
              </div>
            )}
            <div className="OrderConfirmation__items">
              {selectedItemsArray.map(([productType, quantity]) => {
                const productInfo = MEMBERSHIP_OFFERED_ITEMS[productType];
                return (
                  <div key={productType} className="OrderConfirmation__item">
                    <div className="OrderConfirmation__item-image-container">
                      <div className="OrderConfirmation__item-image">
                        {productInfo.image && (
                        <img
                          src={productInfo.image}
                          alt={productInfo.title}
                          className="OrderConfirmation__product-image"
                        />
                        )}
                      </div>
                      <div className="OrderConfirmation__item-quantity">
                        <div className="OrderConfirmation__item-quantity-badge">
                          {quantity}
                        </div>
                      </div>
                    </div>
                    <div className="OrderConfirmation__item-details">
                      <span className="OrderConfirmation__item-name">
                        {productInfo.title}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {showCta && (
          <div className="OrderConfirmation__cta-content">
            <Button
              type={BUTTON_TYPE.LINK_QUATERNARY}
              size={BUTTON_SIZE.LARGE}
              onClick={resetOrder}
              className="OrderConfirmation__cta-button"
            >
              Need more bags & boxes?
            </Button>
          </div>
        )}
        {isOneTimeOrder && !isActiveSubscription && (
          <UnlimitedShortcutBannerCard
            className="OrderConfirmation__membership"
          />
        )}
        {showPromoSection && isSignedIn && (
          <>
            <RewardsV2Shelf
              title={(
                <div className="OrderConfirmation__promo-section-title">
                  Save & Earn
                  <TCCoinIcon />
                </div>
              )}
              type={REWARD_PREVIEW_TYPES.DEFAULT}
              defaultSlidesPerPage={isMobileSize ? 2 : 4}
              backgroundColor="var(--trashie-ui-faded-black-10)"
              itemCardBackgroundColor="var(--trashie-ui-crisp-white)"
              advancedMode
              fullWidth={!isMobileSize}
              mobileGap="0.5rem"
              trackPadding={SLIDER_TRACK_PADDING.MEDIUM}
            />

            <RewardsV2Shelf
              title={(
                <div className="OrderConfirmation__promo-section-title">
                  Earn
                  <TCCoinIcon />
                  with Trending Deals
                </div>
              )}
              type={REWARDS_V2_SHELF_TYPES.HOT}
              defaultSlidesPerPage={isMobileSize ? 2 : 4}
              backgroundColor="var(--trashie-ui-faded-black-10)"
              itemCardBackgroundColor="var(--trashie-ui-crisp-white)"
              advancedMode
              fullWidth={!isMobileSize}
              mobileGap="0.5rem"
              trackPadding={SLIDER_TRACK_PADDING.MEDIUM}
            />

            <GiftCardsPreviewList
              className="OrderConfirmation__gift-cards"
              title={(
                <div className="OrderConfirmation__promo-section-title">
                  Redeem
                  <TCCoinIcon />
                  for Gift Cards
                </div>
              )}
              allowContained
            />
          </>
        )}
      </div>
    </div>
  );
};

export default OrderConfirmation;
